import { Fragment } from "react";

import { Menu, Transition } from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";

import classNames from "../../../utils/classNames";
import usePartMutations from "../../../data/usePartMutations";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import { IPart, PartStatus } from "../../../models/Part";
import { StatusPillColour } from "../../shared/StatusPill";

const statusColour = (status: PartStatus | null | undefined): StatusPillColour => {
  switch (status) {
    case PartStatus["On The Car"]:
      return StatusPillColour.Orange;
    case PartStatus["Pending Dismantle"]:
      return StatusPillColour.Yellow;
    case PartStatus.Dismantled:
      return StatusPillColour.Green;
    case PartStatus.Damaged:
      return StatusPillColour.Red;
    case PartStatus.QC:
      return StatusPillColour.Purple;
    case PartStatus.Missing:
      return StatusPillColour.Yellow;
    case PartStatus.Sold:
      return StatusPillColour.Blue;
    case PartStatus.Returned:
      return StatusPillColour.Red;
    case PartStatus.Deleted:
    default:
      return StatusPillColour.Neutral;
  }
};

const triggerClasses = (colour: StatusPillColour): string => {
  switch (colour) {
    case StatusPillColour.Blue:
      return "bg-blue-50 text-blue-700 ring-blue-200 hover:bg-blue-100";
    case StatusPillColour.Green:
      return "bg-green-50 text-green-700 ring-green-200 hover:bg-green-100";
    case StatusPillColour.Yellow:
      return "bg-yellow-50 text-yellow-700 ring-yellow-200 hover:bg-yellow-100";
    case StatusPillColour.Orange:
      return "bg-orange-50 text-orange-700 ring-orange-200 hover:bg-orange-100";
    case StatusPillColour.Red:
      return "bg-red-50 text-red-700 ring-red-200 hover:bg-red-100";
    case StatusPillColour.Purple:
      return "bg-purple-50 text-purple-700 ring-purple-200 hover:bg-purple-100";
    case StatusPillColour.Neutral:
    default:
      return "bg-gray-50 text-gray-700 ring-gray-200 hover:bg-gray-100";
  }
};

const dotClasses = (colour: StatusPillColour): string => {
  switch (colour) {
    case StatusPillColour.Blue:
      return "bg-blue-500";
    case StatusPillColour.Green:
      return "bg-green-500";
    case StatusPillColour.Yellow:
      return "bg-yellow-500";
    case StatusPillColour.Orange:
      return "bg-orange-500";
    case StatusPillColour.Red:
      return "bg-red-500";
    case StatusPillColour.Purple:
      return "bg-purple-500";
    case StatusPillColour.Neutral:
    default:
      return "bg-gray-400";
  }
};

export default function StockStatusEditor({ part }: { part: IPart }) {
  const queryClient = useQueryClient();
  const { patchPart } = usePartMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Part Status");

  const currentValue = part.partStatus;
  const currentColour = statusColour(currentValue);
  const currentLabel =
    currentValue === null || currentValue === undefined
      ? "N/A"
      : PartStatus[currentValue];

  const options = Object.values(PartStatus).filter(
    (v) => typeof v === "string"
  ) as (keyof typeof PartStatus)[];

  const applyStatus = (value: number) => {
    if (value === currentValue) return;
    patchPart.mutate(
      { id: part.id, partStatus: value, vehicleId: part.vehicleId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["pagedTable"]);
          saveHandlers.onSuccess();
        },
        onError: () => {
          saveHandlers.onError();
        },
      }
    );
  };

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button
        className={classNames(
          "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset transition-colors",
          triggerClasses(currentColour)
        )}
        title="Click to change status"
      >
        <span className={classNames("h-1.5 w-1.5 rounded-full", dotClasses(currentColour))} />
        <span>{currentLabel}</span>
        <ChevronDownIcon className="h-3.5 w-3.5 opacity-70" aria-hidden="true" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-30 mt-2 w-52 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Set Status
          </div>
          {options.map((key) => {
            const value = PartStatus[key] as unknown as number;
            const colour = statusColour(value);
            const isCurrent = value === currentValue;
            return (
              <Menu.Item key={key} disabled={patchPart.isLoading}>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => applyStatus(value)}
                    disabled={patchPart.isLoading}
                    className={classNames(
                      active ? "bg-gray-50" : "",
                      isCurrent ? "font-semibold text-gray-900" : "text-gray-700",
                      "group flex w-full items-center justify-between px-3 py-1.5 text-sm disabled:opacity-50"
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <span
                        className={classNames(
                          "h-2 w-2 rounded-full",
                          dotClasses(colour)
                        )}
                      />
                      {key}
                    </span>
                    {isCurrent && (
                      <CheckIcon
                        className="h-4 w-4 text-primary-600"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                )}
              </Menu.Item>
            );
          })}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
